import React, { useState, useEffect } from "react";
import "./editor.less";
import { useSelector, useDispatch } from "react-redux";
import {
   setTemplateName,
   setTemplateSubject,
   setTemplateSaveable,
   setNewsletterContent,
   setTemplateContent,
   setTemplateCoverPhotoURL,
} from "../../appRedux/actions/Newsletter";
import IntlMessages from "@util/IntlMessages";
import { Switch, Card, Upload, Spin } from "../BP2Components";
import { getFile } from "../../services/restAPI/RestApi";
import projectConfig from "@projectConfig";

import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";

import Editor from "react-simple-code-editor";

import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/themes/prism.css";

// Image resize
Quill.register("modules/imageResize", ImageResize);
//Text direction
Quill.register(Quill.import("attributors/style/direction"), true);
//Alignment
Quill.register(Quill.import("attributors/style/align"), true);
// Font size
const Size = Quill.import("attributors/style/size");

const fontSizes = [
   "0.75em",
   "1em",
   "1.25em",
   "1.5em",
   "1.75em",
   "2em",
   "2.25em",
   "2.5em",
   "9px",
   "10px",
   "11px",
   "12px",
   "13px",
   "14px",
   "15px",
   "16px",
   "17px",
   "18px",
];
const allColors = [
   "#0C5B3A",
   "#115622",
   "#097347",
   "#EF4444",
   "#FFB400",
   "#bbdd40",
   "#1c4387",
   "white",
   "#c7c8c9",
   "#a3a3a3",
   "#7c7e81",
   "#646668",
   "#545454",
   "#040404",
   "#a8f9ff",
   "#00ddf9",
   "#00abe7",
   "#0087ca",
   "#00639b",
   "#003b64",
   "#001932",
   "#cfa5c9",
   "#c38fbc",
   "#b779ae",
   "#ab62a1",
   "#9f4c93",
   "#933586",
   "#6d1b61",
   "#c8f0b9",
   "#90d491",
   "#6cb881",
   "#4e9b77",
   "#347f70",
   "#1f5f63",
   "#103546",
   "#f8a9ac",
   "#f47a7f",
   "#f14a51",
   "#ed1b24",
   "#c51017",
   "#970c13",
   "#67080c",
   "#ffd8a4",
   "#ffd092",
   "#ffb86c",
   "#ffa552",
   "#ff8f23",
   "#fe7c04",
   "#e36d00",
   "#ffe385",
   "#ffdd6c",
   "#ffd754",
   "#ffd13b",
   "#ffcc23",
   "#ffc60a",
   "#edb600",
];

Size.whitelist = fontSizes;
Quill.register(Size, true);

const Parchment = Quill.import("parchment");
class IndentAttributor extends Parchment.Attributor.Style {
   add(node, value) {
      if (value === 0) {
         this.remove(node);
         return true;
      } else {
         return super.add(node, `${value}em`);
      }
   }
}

let IndentStyle = new IndentAttributor("indent", "text-indent", {
   scope: Parchment.Scope.BLOCK,
   whitelist: ["1em", "2em", "3em", "4em", "5em", "6em", "7em", "8em", "9em"],
});

Quill.register(IndentStyle, true);

const { hostCloud } = projectConfig.api;

const NewsletterTemplateEditor = (props) => {
   const dispatch = useDispatch();
   const [simpleEditor, setSimpleEditor] = useState(false);
   const [editorValue, setEditorValue] = useState(null);

   const { newsletterContent, templateSaveable, templateIsNew, coverPhotoURL } = useSelector(
      ({ newsletter }) => newsletter,
   );

   const isCover = coverPhotoURL && typeof coverPhotoURL === "string";

   const content = newsletterContent?.[0]?.content;
   const [code, setCode] = React.useState(content || "");

   const [fileLoading, setFileLoading] = useState(false);
   const [fileURL, setFileURL] = useState(null);
   const [fileId, setFileId] = useState(null);

   useEffect(() => {
      setFileId(isCover ? coverPhotoURL.split("?id=")[1] : null);
      setFileURL(isCover ? coverPhotoURL.split("?id=")[0] : null);
      setCode(content);
   }, []);

   let templateName;
   let subject;
   let editable;
   newsletterContent.forEach((el) => {
      if (el.wId === props.id) {
         templateName = el.templateName;
         subject = el.subject;
         editable = el.templateName && el.templateId ? true : false;
      }
   });

   const onNameChange = (evt) => {
      dispatch(setNewsletterContent({ wId: props.id, templateName: evt.target.value }));
      dispatch(setTemplateName(evt.target.value));
   };
   const onSubChange = (evt) => {
      dispatch(setNewsletterContent({ wId: props.id, subject: evt.target.value }));
      dispatch(setTemplateSubject(evt.target.value));
   };

   const onCoverUpload = (fileId) => {
      setFileLoading(true);
      if (typeof fileId === "string") {
         getFile(fileId)
            .then((res) => {
               const coverData = `${res?.data}?id=${fileId}`;
               setFileId(fileId);
               setFileURL(res?.data);
               dispatch(setTemplateCoverPhotoURL(coverData));
               dispatch(setNewsletterContent({ wId: props.id, coverPhotoURL: coverData }));
            })
            .finally(() => {
               setFileLoading(false);
            });
      }
   };
   const handleRemoveCover = () => {
      dispatch(setTemplateCoverPhotoURL(null));
      dispatch(setNewsletterContent({ wId: props.id, coverPhotoURL: null }));
      setFileId(null);
      setFileURL(null);
   };
   const handleSimpleEditorSwitch = (checked) => {
      setSimpleEditor(checked);
   };

   return (
      <Card className="gx-card gx-p-2">
         <div className="simple-edit-switch gx-pb-2" style={{ textAlign: "left" }}>
            <span className="gx-pr-3">
               {`Simple WYSWIG Editor (Does not support tables and divs)`}:{" "}
            </span>
            <Switch
               checked={simpleEditor}
               onChange={(checked) => handleSimpleEditorSwitch(checked)}
            />
            <span className="gx-pr-3"></span>
         </div>

         <div
            className="bp2-quill-container"
            style={simpleEditor === true ? {} : { display: "none" }}
         >
            <ReactQuill
               theme={"snow"}
               value={editorValue !== null ? editorValue : content || ""}
               modules={{
                  toolbar: [
                     [{ header: "1" }, { header: "2" }, { font: [] }],
                     [{ indent: "-1" }, { indent: "+1" }],
                     [
                        {
                           direction: "rtl",
                        },
                     ],
                     [{ size: fontSizes }],
                     ["bold", "italic", "underline", "strike"],
                     [{ align: "justify" }, { align: "center" }, { align: "right" }],
                     [{ color: allColors }],
                     [{ background: allColors }],
                     ["link", "image"],
                  ],
                  clipboard: {
                     // toggle to add extra line breaks when pasting HTML:
                     matchVisual: false,
                  },
                  imageResize: {
                     parchment: Quill.import("parchment"),
                     modules: ["Resize", "DisplaySize"],
                  },
               }}
               formats={[
                  "header",
                  "size",
                  "bold",
                  "background",
                  "italic",
                  "underline",
                  "strike",
                  "align",
                  "justify",
                  "center",
                  "indent",
                  "direction",
                  "right",
                  "color",
                  "link",
                  "image",
               ]}
               onBlur={(e) => {
                  //setCode(editorValue);
               }}
               onChange={(e, delta, user) => {
                  if (user === "user") {
                     setEditorValue(e);
                     dispatch(setNewsletterContent({ wId: props.id, content: e }));
                     dispatch(setTemplateContent(e));
                     setCode(e);
                  }
               }}
            />
         </div>
         <div
            style={
               simpleEditor === true
                  ? { display: "none" }
                  : { border: "1px solid #a3a3a3", minHeight: 100 }
            }
         >
            <div
               dangerouslySetInnerHTML={{
                  __html: editorValue !== null ? editorValue : content,
               }}
            ></div>
         </div>
         <p className="gx-pt-4" style={{ textAlign: "left" }}>
            HTML:
         </p>
         <div
            className="bp2-code-editor"
            style={{
               minHeight: 38,
               maxHeight: 650,
               overflowY: "auto",
               border: "1px solid #a3a3a3",
               background: "rgb(248, 248, 248)",
            }}
         >
            <Editor
               value={code}
               onValueChange={(code) => {
                  setCode(code);
                  // For now we need to remove code formatting. We need better backend to write formatted code
                  const cleanCode = code.replaceAll(`${String.fromCharCode(92)}n`, "");
                  setEditorValue(cleanCode);
                  dispatch(setNewsletterContent({ wId: props.id, content: cleanCode }));
                  dispatch(setTemplateContent(cleanCode));
               }}
               highlight={(code) => highlight(code, languages.js)}
               padding={10}
               style={{
                  fontFamily: '"Fira code", "Fira Mono", monospace',
                  fontSize: 13,
               }}
            />
         </div>
         <div className="titles-container gx-pt-5">
            <strong>
               <IntlMessages id="w.newsletter.templateName" />:{" "}
            </strong>
            <input
               className="ant-input name-input gx-mb-1"
               type="text"
               value={templateName}
               onChange={onNameChange}
               disabled={editable}
            />

            <label>
               <IntlMessages id="w.newsletter.templateSubject" />:{" "}
            </label>
            <input
               className="ant-input name-input"
               type="text"
               value={subject}
               onChange={onSubChange}
            />
            <label>
               <IntlMessages id="newsletter.newsletterCover" />:{" "}
            </label>

            <Upload
               children="Upload"
               uploadUrl={`${hostCloud}/file`}
               onUpload={onCoverUpload}
               defaultFileId={fileId}
               className="gx-pt-4"
               cloudServer={true}
               defaultFileUrl={fileURL}
               onRemove={handleRemoveCover}
               fileType="image"
            >
               <span className="ant-upload-drag-icon gx-pl-2" style={{ cursor: "pointer" }}>
                  <IntlMessages id="upload.clickOrDragFile" />
                  <i className="icon icon-upload gx-ml-1" />
                  {fileLoading ? <Spin></Spin> : null}
               </span>
            </Upload>
         </div>
         <div className="update-switch gx-pt-3" style={{ textAlign: "left" }}>
            <span className="gx-pr-3">
               <IntlMessages id="templateEditor.update" />
            </span>
            <Switch
               disabled={templateIsNew ? true : false}
               checked={templateSaveable ? true : false}
               onChange={(checked) => dispatch(setTemplateSaveable(checked))}
            />
         </div>
      </Card>
   );
};

export default NewsletterTemplateEditor;
